/** @file corners: the screen quad of one impostor with per-corner depth and cut distance (design 5, 21.6). */
import type { Corners, CornersResult, PerCorner } from '@zilo/pose3d-render-core/contract';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import { viewOf } from '@zilo/pose3d-render-core/camera/viewOf';
import { cutStretchOf } from '@zilo/pose3d-render-core/corners/cutStretchOf';
import { rotateLocal } from '@zilo/pose3d-render-core/corners/rotateLocal';
import { twoPointFrameOf } from '@zilo/pose3d-render-core/corners/twoPointFrameOf';
import { twoPointQuadOf } from '@zilo/pose3d-render-core/corners/twoPointQuadOf';
import { anchorDirOf } from '@zilo/pose3d-render-core/cut/anchorDirOf';
import { edgeDistanceOf } from '@zilo/pose3d-render-core/cut/edgeDistanceOf';
import { edgeOfNormal } from '@zilo/pose3d-render-core/cut/edgeOfNormal';
import { imageDirOf } from '@zilo/pose3d-render-core/cut/imageDirOf';
import { pivot2Of } from '@zilo/pose3d-render-core/cut/pivot2Of';
import { TILE_EDGES } from '@zilo/pose3d-render-core/cut/tileEdges';
import { depthSlopeOf } from '@zilo/pose3d-render-core/depth/depthSlopeOf';
import { quadDepth } from '@zilo/pose3d-render-core/depth/quadDepth';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';

/** The slots of one stride the quad reads. */
type QuadSlots = {
  a: [number, number]; b: [number, number]; pivot: [number, number]; size: [number, number]; rotate: number;
  cutMode: number; cutAngle: number; cutPos: number; cutOff: number; viewport: [number, number];
};

/** One-point: `A' + rotate(q - pivot, rotate)`. */
function onePointQuad(s: QuadSlots): PerCorner<[number, number]> {
  const [w, h] = s.size;
  const local: PerCorner<[number, number]> = [[0, 0], [w, 0], [w, h], [0, h]];
  return local.map((q): [number, number] => {
    const r = rotateLocal(q[0] - s.pivot[0], q[1] - s.pivot[1], s.rotate);
    return [s.a[0] + r[0], s.a[1] + r[1]];
  }) as PerCorner<[number, number]>;
}

/** Two-point (flag 4, 21.6): pivot2 from `rotate` inset by `cutOff`, theta from W, the geometry, the texel crop. */
function twoPointQuad(s: QuadSlots): PerCorner<[number, number]> {
  const p2 = pivot2Of(s.pivot, anchorDirOf(s.rotate), s.size[0], s.size[1], s.cutOff);
  const { theta } = twoPointFrameOf(s.a, s.b, s.pivot, p2.point, s.viewport);
  const quad = twoPointQuadOf(s.a, s.b, s.pivot, p2, s.size, theta);
  if (s.cutMode !== 1) return quad;
  const edge = edgeOfNormal(s.cutAngle);
  const extent = s.size[TILE_EDGES[edge].axis];
  return cutStretchOf(quad, edge, extent, edgeDistanceOf(edge, s.pivot, s.size[0], s.size[1]) - s.cutPos);
}

/**
 * The full tile quad `[tl, tr, br, bl]`. One-point: local corners minus the pivot turned by the `rotate` slot, added to
 * A'. Two-point (flag 4): the pivot2-edge corners from B', the others from A', turned by the W-weighted theta, and with
 * `cutMode 1` the cut edge's corners pushed out so the kept texels fill that geometry (a texture crop, 21.6). Depth per
 * corner = `quadDepth + depthSlopeOf(sheer, pitch) (A'.y - corner.y) / ppu` (15.2); `cutDist` = `q . imageDirOf(cutAngle)
 * - cutPos` on the local corner `q` (corner minus pivot) for `cutMode 1`, else -1 (draw where <= 0).
 */
export const corners: Corners = (list, index, camera, profile) => {
  const o = index * L.stride;
  const at = (k: number): number => list[o + k] ?? 0;
  const view = viewOf(camera, profile);
  const s: QuadSlots = {
    a: screenOf([at(L.ax), at(L.ay), at(L.az)], view), b: screenOf([at(L.bx), at(L.by), at(L.bz)], view),
    pivot: [at(L.pivotX), at(L.pivotY)], size: [at(L.texW), at(L.texH)], rotate: at(L.rotate),
    cutMode: at(L.cutMode), cutAngle: at(L.cutAngle), cutPos: at(L.cutPos), cutOff: at(L.cutOff), viewport: camera.viewport,
  };
  const quad = (at(L.flags) & 4) !== 0 ? twoPointQuad(s) : onePointQuad(s);
  const [w, h] = s.size;
  const [px, py] = s.pivot;
  const local: PerCorner<[number, number]> = [[-px, -py], [w - px, -py], [w - px, h - py], [-px, h - py]];
  const n = imageDirOf(s.cutAngle);
  const base = quadDepth(list, index, view);
  const slope = depthSlopeOf(at(L.sheer), camera.pitchDeg) / camera.pixelsPerUnit;
  const result: CornersResult = {
    corners: quad,
    depths: quad.map((c) => base + slope * (s.a[1] - c[1])) as PerCorner<number>,
    cutDist: local.map((q) => (s.cutMode === 1 ? q[0] * n[0] + q[1] * n[1] - s.cutPos : -1)) as PerCorner<number>,
    tex: [at(L.texX), at(L.texY), w, h],
  };
  return result;
};
